import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Plus, Trash2, Ban, RefreshCw, KeyRound, Shield, X, ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import MultiClientPicker from "@/components/MultiClientPicker";

const ROLE_BADGES = {
    super_admin: "bg-[#7C3AED]/15 border-[#7C3AED]/40 text-[#7C3AED]",
    admin: "bg-blue-400/10 border-blue-400/30 text-blue-400",
    viewer: "bg-white/5 border-white/20 text-white/70",
};

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function AdminsPage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOpen, setNewOpen] = useState(false);
    const [sigOpen, setSigOpen] = useState(null); // { action, user }
    const [resetOpen, setResetOpen] = useState(null);
    const [permsOpen, setPermsOpen] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/admins");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const doReactivate = async (u) => {
        try {
            await api.post(`/admin/admins/${u.id}/reactivate`);
            toast.success("Reactivated");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const doResendInvite = async (u) => {
        try {
            const { data } = await api.post(`/admin/admins/${u.id}/resend-invite`);
            if (data?.email?.ok) {
                toast.success(`Invite re-sent to ${u.email}`);
            } else if (data?.accept_url) {
                navigator.clipboard.writeText(data.accept_url);
                toast.success(
                    `Email failed — link copied to clipboard. Share it with ${u.email} manually.`,
                    { duration: 6000 },
                );
            } else {
                toast.warning("Invite re-issued, but the email could not be sent.");
            }
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="px-8 py-8">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Access Management</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2 flex items-center gap-3">
                        Administrators
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${ROLE_BADGES[user?.role] || ""}`}>
                            you are: {user?.role}
                        </span>
                    </h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Manage who can access this console. All mutating actions are
                        recorded in the tamper-evident audit log. Dangerous actions
                        require 21 CFR Part 11 electronic signatures.
                    </p>
                </div>
                {isSuper && (
                    <button
                        onClick={() => setNewOpen(true)}
                        data-testid="admin-new"
                        className="bg-[#7C3AED] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.8} /> Invite admin
                    </button>
                )}
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-5 py-3">Email</th>
                            <th className="label-xs px-5 py-3">Name</th>
                            <th className="label-xs px-5 py-3">Role</th>
                            <th className="label-xs px-5 py-3">Status</th>
                            <th className="label-xs px-5 py-3">Last login</th>
                            <th className="label-xs px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : items.map((u) => (
                            <tr key={u.id} className="data-row border-t border-white/5" data-testid={`admin-row-${u.id}`}>
                                <td className="px-5 py-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        {u.role === "super_admin" && <Shield className="w-3.5 h-3.5 text-[#7C3AED]" strokeWidth={1.8} />}
                                        <span className="font-mono">{u.email}</span>
                                        {u.must_change_password && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-400/10 border border-amber-400/30 text-amber-400 font-mono">must change pw</span>}
                                    </div>
                                </td>
                                <td className="px-5 py-3 text-sm">{u.name}</td>
                                <td className="px-5 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${ROLE_BADGES[u.role] || ROLE_BADGES.viewer}`}>
                                        {u.role}
                                    </span>
                                </td>
                                <td className="px-5 py-3">
                                    {u.status === "active" ? (
                                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-400/30 bg-emerald-400/10 text-emerald-400 rounded-sm">active</span>
                                    ) : (
                                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-red-400/30 bg-red-400/10 text-red-400 rounded-sm">{u.status}</span>
                                    )}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs text-white/60">
                                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}
                                </td>
                                <td className="px-5 py-3">
                                    <div className="flex items-center justify-end gap-1.5">
                                        {isSuper && u.id !== user?.id && (
                                            <>
                                                <button
                                                    onClick={() => setPermsOpen(u)}
                                                    title="Edit permissions"
                                                    data-testid={`admin-perms-${u.id}`}
                                                    className="p-1.5 rounded hover:bg-[#7C3AED]/20 text-white/70 hover:text-[#7C3AED]"
                                                >
                                                    <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                                {u.status === "pending" && (
                                                    <button
                                                        onClick={() => doResendInvite(u)}
                                                        title="Re-send invite email"
                                                        data-testid={`admin-resend-invite-${u.id}`}
                                                        className="p-1.5 rounded hover:bg-[#7C3AED]/20 text-white/70 hover:text-[#a17bff]"
                                                    >
                                                        <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setResetOpen(u)}
                                                    title="Reset password"
                                                    data-testid={`admin-reset-${u.id}`}
                                                    className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
                                                >
                                                    <KeyRound className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                                {u.status === "active" ? (
                                                    <button
                                                        onClick={() => setSigOpen({ action: "suspend", user: u })}
                                                        title="Suspend"
                                                        data-testid={`admin-suspend-${u.id}`}
                                                        className="p-1.5 rounded hover:bg-amber-500/20 text-white/70 hover:text-amber-400"
                                                    >
                                                        <Ban className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => doReactivate(u)}
                                                        title="Reactivate"
                                                        data-testid={`admin-reactivate-${u.id}`}
                                                        className="p-1.5 rounded hover:bg-emerald-500/20 text-white/70 hover:text-emerald-400"
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setSigOpen({ action: "delete", user: u })}
                                                    title="Delete"
                                                    data-testid={`admin-delete-${u.id}`}
                                                    className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-16 text-center">
                                <Users className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                <div className="text-sm text-white/60">No admin users yet</div>
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {newOpen && <NewAdminModal onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); load(); }} />}
            {sigOpen && <SignatureModal data={sigOpen} onClose={() => setSigOpen(null)} onDone={() => { setSigOpen(null); load(); }} />}
            {resetOpen && <ResetPasswordModal user={resetOpen} onClose={() => setResetOpen(null)} onDone={() => { setResetOpen(null); load(); }} />}
            {permsOpen && <PermissionsModal user={permsOpen} onClose={() => setPermsOpen(null)} onDone={() => { setPermsOpen(null); load(); }} />}
        </div>
    );
}

function NewAdminModal({ onClose, onCreated }) {
    const [mode, setMode] = useState("invite_link");  // 'invite_link' | 'inline_password'
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("admin");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "invite_link") {
                const { data } = await api.post("/admin/admins/invite", {
                    email: email.toLowerCase().trim(),
                    name: name.trim() || email.split("@")[0],
                    role,
                });
                if (data?.email?.ok) {
                    toast.success(`Invite emailed to ${email}.`);
                    onCreated();
                } else {
                    // Fallback: surface the magic link to copy/paste
                    setResult(data);
                }
            } else {
                await api.post("/admin/admins", {
                    email: email.toLowerCase().trim(),
                    name: name.trim() || "Admin",
                    role, password,
                });
                toast.success(`Created ${email}. They must change password on first login.`);
                onCreated();
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (result) {
        // Email send failed — render the magic-link-copy view
        return (
            <ModalShell title="Invite created — email failed" onClose={onClose} testId="new-admin-modal">
                <div className="space-y-4">
                    <div className="bg-amber-400/10 border border-amber-400/30 rounded-md p-3 text-xs text-amber-300">
                        The admin row was created, but the invite email could not be delivered
                        ({result?.email?.error || result?.email?.reason || "unknown"}).
                        Share this single-use link with <strong className="text-white">{email}</strong> manually.
                        It expires {(result?.expires_at || "").replace("T", " ").slice(0, 19)} UTC.
                    </div>
                    <div className="bg-[#050505] border border-white/10 rounded-md p-3 break-all text-xs font-mono text-[#a17bff]" data-testid="invite-fallback-url">
                        {result.accept_url}
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(result.accept_url); toast.success("Copied"); }}
                            className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">
                            Copy link
                        </button>
                        <button onClick={onCreated} data-testid="invite-fallback-done"
                            className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium">
                            Done
                        </button>
                    </div>
                </div>
            </ModalShell>
        );
    }

    return (
        <ModalShell title="Invite admin" onClose={onClose} testId="new-admin-modal">
            {/* Mode toggle */}
            <div className="flex p-1 bg-[#050505] border border-white/10 rounded-md mb-4 text-xs font-mono">
                <button type="button" onClick={() => setMode("invite_link")}
                    data-testid="invite-mode-link"
                    className={`flex-1 py-1.5 rounded-sm transition-colors ${mode === "invite_link" ? "bg-[#7C3AED] text-white" : "text-white/60 hover:text-white"}`}>
                    Email magic link
                </button>
                <button type="button" onClick={() => setMode("inline_password")}
                    data-testid="invite-mode-password"
                    className={`flex-1 py-1.5 rounded-sm transition-colors ${mode === "inline_password" ? "bg-[#7C3AED] text-white" : "text-white/60 hover:text-white"}`}>
                    Inline password
                </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
                <Input label="Email" type="email" value={email} onChange={setEmail} required testId="new-admin-email" />
                <Input label="Name" value={name} onChange={setName} testId="new-admin-name" />
                <div>
                    <div className="label-xs mb-1.5">Role</div>
                    <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="new-admin-role"
                        className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                        <option value="admin">admin</option>
                        <option value="viewer">viewer</option>
                        <option value="super_admin">super_admin</option>
                    </select>
                </div>
                {mode === "inline_password" ? (
                    <Input label="One-time password" type="password" value={password} onChange={setPassword} required testId="new-admin-password"
                        hint="Min 12 chars, upper + lower + digit + symbol. User will be forced to change on first login." />
                ) : (
                    <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/20 rounded-md p-3 text-xs text-white/70">
                        We'll email the new admin a single-use link (valid 72h). They click → set their own password → activate.
                        No password gets typed by you.
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="new-admin-submit"
                        className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {loading ? (mode === "invite_link" ? "Sending…" : "Creating…") : (mode === "invite_link" ? "Send invite email" : "Create admin")}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function SignatureModal({ data, onClose, onDone }) {
    const [actorPassword, setActorPassword] = useState("");
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const { action, user } = data;

    const submit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            toast.error("Reason is required");
            return;
        }
        setLoading(true);
        try {
            if (action === "suspend") {
                await api.post(`/admin/admins/${user.id}/suspend`, { actor_password: actorPassword, reason });
                toast.success(`Suspended ${user.email}`);
            } else if (action === "delete") {
                await api.delete(`/admin/admins/${user.id}?actor_password=${encodeURIComponent(actorPassword)}&reason=${encodeURIComponent(reason)}`);
                toast.success(`Deleted ${user.email}`);
            }
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalShell title={`Confirm ${action} — ${user.email}`} onClose={onClose} testId="esig-modal">
            <div className="mb-4 p-3 border border-amber-400/30 bg-amber-400/5 rounded-md text-xs text-amber-400 flex items-start gap-2">
                <Shield className="w-3.5 h-3.5 mt-0.5" strokeWidth={1.8} />
                <div>
                    <div className="font-medium">21 CFR Part 11 electronic signature</div>
                    <div className="text-amber-400/80 mt-1">
                        Re-enter your password and provide a business reason. This action will
                        be recorded in the tamper-evident audit log.
                    </div>
                </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
                <Input label="Your password" type="password" required value={actorPassword} onChange={setActorPassword} testId="esig-password" />
                <div>
                    <div className="label-xs mb-1.5">Business reason *</div>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} required data-testid="esig-reason"
                        placeholder="e.g. Employee left the company on 2026-02-14" className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-white/30" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="esig-submit"
                        className={`px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40 ${action === "delete" ? "bg-red-500 text-white hover:bg-red-600" : "bg-amber-500 text-black hover:bg-amber-400"}`}>
                        {loading ? "Signing…" : `Sign & ${action}`}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function ResetPasswordModal({ user, onClose, onDone }) {
    const [newPassword, setNewPassword] = useState("");
    const [actorPassword, setActorPassword] = useState("");
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) { toast.error("Reason is required"); return; }
        setLoading(true);
        try {
            await api.post(`/admin/admins/${user.id}/reset-password`, { new_password: newPassword, actor_password: actorPassword, reason });
            toast.success(`Password reset for ${user.email}. They must change on next login.`);
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally { setLoading(false); }
    };
    return (
        <ModalShell title={`Reset password — ${user.email}`} onClose={onClose} testId="reset-pw-modal">
            <form onSubmit={submit} className="space-y-4">
                <Input label="New password (temp)" type="password" required value={newPassword} onChange={setNewPassword} testId="reset-pw-new" hint="Min 12 chars, mixed case + digit + symbol" />
                <Input label="Your password (e-signature)" type="password" required value={actorPassword} onChange={setActorPassword} testId="reset-pw-esig" />
                <Input label="Reason" required value={reason} onChange={setReason} testId="reset-pw-reason" />
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="reset-pw-submit" className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {loading ? "Resetting…" : "Reset password"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function ModalShell({ title, onClose, children, testId, maxWidth = "max-w-md" }) {
    // Track where mousedown started so we don't dismiss the modal when a user
    // begins selecting text inside it and releases the mouse on the backdrop.
    const downOnBackdrop = React.useRef(false);
    const handleBackdropMouseDown = (e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
    };
    const handleBackdropMouseUp = (e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose();
        downOnBackdrop.current = false;
    };
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
            onMouseDown={handleBackdropMouseDown}
            onMouseUp={handleBackdropMouseUp}
        >
            <div className={`w-full ${maxWidth} max-h-[90vh] overflow-hidden bg-[#0a0a0a] border border-white/15 rounded-md flex flex-col`} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()} data-testid={testId}>
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <div className="font-display text-lg tracking-tight">{title}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" strokeWidth={1.6} /></button>
                </div>
                <div className="p-5 overflow-auto">{children}</div>
            </div>
        </div>
    );
}

function Input({ label, hint, type = "text", value, onChange, required, testId }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>
            <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
            {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
        </div>
    );
}

function PermissionsModal({ user: target, onClose, onDone }) {
    const [catalog, setCatalog] = useState(null);
    const [extra, setExtra] = useState(new Set());
    const [denied, setDenied] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [originalRolePerms, setOriginalRolePerms] = useState(new Set());
    const [clientIdsArr, setClientIdsArr] = useState([]);
    const [originalClientIds, setOriginalClientIds] = useState([]);
    const [savingClientIds, setSavingClientIds] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const [permsRes, adminRes] = await Promise.all([
                    api.get("/admin/permissions"),
                    api.get(`/admin/admins/${target.id}`),
                ]);
                setCatalog(permsRes.data);
                const role = adminRes.data?.role || target.role;
                const roleRow = (permsRes.data?.roles || []).find((r) => r.key === role);
                setOriginalRolePerms(new Set(roleRow?.permissions || []));
                setExtra(new Set(adminRes.data?.extra_permissions || []));
                setDenied(new Set(adminRes.data?.denied_permissions || []));
                const cids = adminRes.data?.client_ids || [];
                setClientIdsArr(cids);
                setOriginalClientIds(cids);
            } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [target.id, target.role]);

    const togglePerm = (key) => {
        const inRole = originalRolePerms.has(key);
        // Tri-state per key (when role grants it):
        //   denied → role default (granted) → role default (granted)  (only 2 states for role-granted)
        // For role-not-granted:
        //   none → extra (granted) → none
        if (inRole) {
            // Role grants it — toggle deny
            const nextDenied = new Set(denied);
            if (nextDenied.has(key)) nextDenied.delete(key);
            else nextDenied.add(key);
            setDenied(nextDenied);
            // Make sure extra is clean
            const nextExtra = new Set(extra);
            nextExtra.delete(key);
            setExtra(nextExtra);
        } else {
            // Role does NOT grant it — toggle extra
            const nextExtra = new Set(extra);
            if (nextExtra.has(key)) nextExtra.delete(key);
            else nextExtra.add(key);
            setExtra(nextExtra);
            // denied no-op for non-role perms
            const nextDenied = new Set(denied);
            nextDenied.delete(key);
            setDenied(nextDenied);
        }
    };

    const isGranted = (key) => {
        if (target.role === "super_admin") return true;
        const inRole = originalRolePerms.has(key);
        if (inRole && denied.has(key)) return false;
        if (inRole) return true;
        return extra.has(key);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/admins/${target.id}/permissions`, {
                extra_permissions: Array.from(extra),
                denied_permissions: Array.from(denied),
            });
            toast.success(`Permissions updated for ${target.email}`);
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const saveClientIds = async () => {
        setSavingClientIds(true);
        try {
            const cleaned = (clientIdsArr || []).map((c) => (c || "").trim()).filter(Boolean);
            await api.patch(`/admin/admins/${target.id}/client-ids`, { client_ids: cleaned });
            toast.success(`Tenant scoping updated for ${target.email}`);
            setOriginalClientIds(cleaned);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSavingClientIds(false);
        }
    };

    const grouped = useMemo(() => {
        if (!catalog) return [];
        const map = new Map();
        for (const p of catalog.permissions || []) {
            if (!map.has(p.category)) map.set(p.category, []);
            map.get(p.category).push(p);
        }
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [catalog]);

    return (
        <ModalShell title={`Permissions — ${target.email}`} onClose={onClose} testId="perms-modal" maxWidth="max-w-3xl">
            {loading ? (
                <div className="text-xs text-white/40 font-mono py-6 text-center">Loading…</div>
            ) : (
                <div className="space-y-4">
                    <div className="p-3 border border-[#7C3AED]/30 bg-[#7C3AED]/5 rounded-md text-xs text-white/80 flex items-start gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-[#7C3AED]" strokeWidth={1.8} />
                        <div>
                            <div className="font-medium text-white">Effective permissions for role &quot;{target.role}&quot;</div>
                            <div className="text-white/60 mt-1">
                                Toggle a checkbox to <strong>grant</strong> a permission the role doesn&apos;t already have
                                (added to <code className="text-white/80">extra_permissions</code>) or to <strong>deny</strong> a
                                role-granted permission (added to <code className="text-white/80">denied_permissions</code>).
                                <span className="text-[#7C3AED]"> super_admin</span> always has every permission and cannot be edited.
                            </div>
                        </div>
                    </div>

                    {/* Tenant scoping — Phase K */}
                    <div className="border border-white/10 rounded-md p-3" data-testid="perms-tenant-scope">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="label-xs">Tenant scoping (client_ids)</span>
                            <span className="text-[10px] uppercase tracking-wider font-mono text-white/40">
                                {target.role === "super_admin" ? "super_admin = wildcard (all tenants)" : "select one or more"}
                            </span>
                        </div>
                        {target.role === "super_admin" ? (
                            <div className="text-[11px] text-white/40 font-mono py-2 px-1">
                                (super_admin sees all tenants)
                            </div>
                        ) : (
                            <>
                                <MultiClientPicker
                                    value={clientIdsArr}
                                    onChange={setClientIdsArr}
                                    testIdPrefix="perms-client-ids"
                                />
                                <div className="flex justify-end mt-2">
                                    <button
                                        onClick={saveClientIds}
                                        disabled={
                                            savingClientIds
                                            || JSON.stringify([...clientIdsArr].sort())
                                                === JSON.stringify([...originalClientIds].sort())
                                        }
                                        data-testid="perms-client-ids-save"
                                        className="px-3 py-2 text-xs bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-40"
                                    >
                                        {savingClientIds ? "Saving…" : "Save scope"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {target.role === "super_admin" ? (
                        <div className="text-xs text-white/50 font-mono py-6 text-center">
                            super_admin role cannot be restricted via overrides.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {grouped.map(([category, perms]) => (
                                <div key={category} data-testid={`perms-cat-${category.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="label-xs text-white/50 mb-2">{category}</div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {perms.map((p) => {
                                            const granted = isGranted(p.key);
                                            const inRole = originalRolePerms.has(p.key);
                                            const isExtra = extra.has(p.key);
                                            const isDenied = denied.has(p.key);
                                            return (
                                                <label
                                                    key={p.key}
                                                    data-testid={`perm-${p.key}`}
                                                    className={`flex items-start gap-2 p-2 border rounded-md cursor-pointer transition-colors ${granted ? "border-[#7C3AED]/40 bg-[#7C3AED]/5" : "border-white/10 hover:bg-white/5"}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={granted}
                                                        onChange={() => togglePerm(p.key)}
                                                        className="mt-0.5"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-mono text-white/85 flex items-center gap-1.5 flex-wrap">
                                                            {p.key}
                                                            {inRole && !isDenied && <span className="text-[9px] tracking-wider uppercase text-[#7C3AED]/80">role</span>}
                                                            {isExtra && <span className="text-[9px] tracking-wider uppercase text-emerald-300">extra</span>}
                                                            {isDenied && <span className="text-[9px] tracking-wider uppercase text-red-300">denied</span>}
                                                        </div>
                                                        <div className="text-[11px] text-white/50">{p.description}</div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                        <button onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Close</button>
                        {target.role !== "super_admin" && (
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="perms-save"
                                className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40"
                            >
                                {saving ? "Saving…" : "Save permissions"}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </ModalShell>
    );
}
